import { firestore } from '@utils/firebase'
import {
  collection,
  endBefore,
  getAggregateFromServer,
  getCountFromServer,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  startAfter,
  sum,
  where,
} from 'firebase/firestore'
import moment from 'moment'
import { firestoreLikeValueToJsDate } from '@utils/datetime'
import {
  Donation,
  RecurringDonation,
  GetSelectedUserDonationsParams,
  DonationHistoryResult,
} from '@Interfaces/Data/DonationInterface'
import { Org } from '@Interfaces/Data/OrganizationInterface'
import { User } from '@Interfaces/Data/UserInterface'
import { ApiResponse, ApiListResponse } from '@Interfaces/Api/ResponsesInterface'
import { ApiError } from '@Interfaces/Api/ErrorsInterface'
import {
  UnsubscribeFunction,
  ListenerCallback,
  ErrorCallback,
} from '@Interfaces/Services/ServiceTypes'

/**
 * Listen for recurring donation changes
 * Returns unsubscribe function
 */
export const listenForRecurringDonationChanges = (
  org: Org,
  onUpdate: ListenerCallback<RecurringDonation[]>,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('donations_recurring')
    .where('orgId', '==', org.id)
    .where('active', '==', true)
    .orderBy('startDate', 'desc')
    .onSnapshot(
      (querySnapshot) => {
        const donations: RecurringDonation[] = []

        querySnapshot.forEach((doc) => {
          donations.push({
            id: doc.id,
            ...doc.data(),
          } as RecurringDonation)
        })

        onUpdate(donations)
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message,
          details: error,
        })
      }
    )
}

/**
 * Get recent donations for an organization
 */
export const getRecentDonations = async (selectedOrg: Org): Promise<ApiResponse<Donation[]>> => {
  try {
    const querySnapshot = await firestore
      .collection('donations')
      .where('orgId', '==', selectedOrg?.id)
      .orderBy('date', 'desc')
      .get()

    const history: Donation[] = []
    querySnapshot.forEach((doc) => {
      history.push({
        ...doc.data(),
        id: doc.id,
      } as Donation)
    })

    return {
      success: true,
      data: history,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get recent donations',
      data: undefined,
    }
  }
}

/**
 * Get selected user donations with pagination
 */
export const getSelectedUserDonations = async ({
  userId,
  donations = [],
  rowsPerPage,
  currentPage,
  donationsCount,
  nextPage,
}: GetSelectedUserDonationsParams): Promise<ApiResponse<DonationHistoryResult>> => {
  try {
    const coll = collection(firestore, 'donations')

    const queries: any[] = []

    queries.push(where('userId', '==', userId))

    const countQuery = query(coll, ...queries)
    const totalCount = await getCountFromServer(countQuery)

    const go =
      nextPage === 0
        ? 'first'
        : donationsCount && nextPage === Math.ceil(donationsCount / rowsPerPage)
          ? 'last'
          : nextPage > currentPage
            ? 'next'
            : nextPage < currentPage
              ? 'previous'
              : 'first'

    const rowsLimit = go === 'last' ? donationsCount % rowsPerPage : rowsPerPage

    queries.push(orderBy('date', 'desc'))

    if (go === 'first') queries.push(limit(rowsLimit || 10))

    if (go === 'last') queries.push(limitToLast(rowsLimit || 10))

    if (go === 'next' && donations.length)
      queries.push(
        startAfter(moment(donations[donations.length - 1]?.date).toDate()),
        limit(rowsLimit || 10)
      )

    if (go === 'previous' && donations.length)
      queries.push(endBefore(moment(donations[0]?.date).toDate()), limitToLast(rowsLimit || 10))

    const dataQuery = query(coll, ...queries)
    const snapshot = await getDocs(dataQuery)

    if (snapshot.empty) {
      return {
        success: false,
        error: 'No donations found',
        data: undefined,
      }
    }

    const newDonations = snapshot.docs.map((doc) => {
      const { recurring, amount, date, appClip } = doc.data()

      return {
        id: doc.id,
        source: appClip ? 'Website' : 'App',
        frequency: recurring ? 'recurring' : 'one-time',
        amount,
        date: moment(date.toDate()).format('MM/DD/YYYY'),
      }
    })

    const sumQuery = query(coll, where('userId', '==', userId))
    const totalDonation = await getAggregateFromServer(sumQuery, {
      totalDonation: sum('amount'),
    })

    const results: DonationHistoryResult = {
      donationsCount: totalCount.data().count,
      donations: newDonations,
      totalDonation: totalDonation.data().totalDonation,
      nextPage: newDonations.length > 0 ? newDonations.length : 0,
    }

    return {
      success: true,
      data: results,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get user donations',
      data: undefined,
    }
  }
}

/**
 * Get donation history by organization ID and user ID
 */
export const getDonationHistoryByOrgIdAndUserId = async (
  selectedUser: User,
  selectedOrg: Org
): Promise<ApiResponse<Donation[]>> => {
  try {
    const querySnapshot = await firestore
      .collection('donations')
      .where('orgId', '==', selectedOrg.id)
      .where('userId', '==', selectedUser.id)
      .get()

    const history: Donation[] = []
    querySnapshot.forEach((doc) => {
      const item = {
        ...doc.data(),
        id: doc.id,
      } as Donation
      item.date = firestoreLikeValueToJsDate(item.date) as Donation['date']
      history.push(item)
    })

    history.sort((a, b) => {
      const dateA = firestoreLikeValueToJsDate(a.date)
      const dateB = firestoreLikeValueToJsDate(b.date)
      return dateB.getTime() - dateA.getTime()
    })

    return {
      success: true,
      data: history,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get donation history',
      data: undefined,
    }
  }
}
